import { config } from '@config';
import { RxUtils, TypeUtils } from '@utils';
import { merge, of } from 'rxjs';
import { concatMap, tap, map, catchError, toArray } from 'rxjs/operators';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { UserInvitation } from './user-invitation.class';
import { UserInvitationState } from './user-invitation-state.enum';
import { InviteUserDto } from './invite-user.dto';

export class UserInvitationService {
	private getUrl = () => `${config.apiUrl}/user/invite`;

	get(idOrEmail: string) {
		return httpService.get<UserInvitation>(this.getUrl(), idOrEmail, { noAuth: true }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.get.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(UserInvitation, result))
		);
	}

	getAll(state?: UserInvitationState) {
		return httpService.get<UserInvitation[]>(this.getUrl(), ['invitations', state].filter(Boolean).join('/')).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getAll.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(UserInvitation, result))
		);
	}

	invite(dto: InviteUserDto) {
		return httpService.post<unknown>(this.getUrl(), '', { body: dto, errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.invite.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}

	update({ id, ...user }: Partial<UserInvitation>) {
		return httpService.patch<unknown>(this.getUrl(), `${id}`, { body: user, errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.update.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}

	delete(email: string) {
		return httpService.delete<unknown>(this.getUrl(), email, { errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.delete.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}

	deleteMultiple(emails: string[]) {
		return merge(...emails.map((e) => this.delete(e))).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.deleteMultiple.name}]`, result)),
			catchError((e) => {
				logger.error(e);
				return of(null);
			}),
			toArray()
		);
	}

	accept(id: string, name: string, surname: string, password: string) {
		return httpService
			.post<unknown>(this.getUrl(), `${id}/accept`, { body: { name, surname, password }, noAuth: true, errorHandler: 'notification' })
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.accept.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
			);
	}

	resendInvitation(email: string) {
		return httpService.post<unknown>(this.getUrl(), `resend/${email}`, { errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.resendInvitation.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}
}

export const userInvitationService = new UserInvitationService();
